import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAsync from 'react-use/lib/useAsync';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { Grid, Radio, RadioGroup, TextField } from '@backstage/ui';
import { 
  DropdownField,
  RequiredStar,
  useDropdownField,
  StepTimeline
} from '@internal/plugin-shared-react';
import shared from '../../../styles.module.css';
import styles from './CreateDevBoxServiceGroupPage.module.css';

/** Step labels of the create flow. */
export const devBoxToolingGroupSteps = [
  'General information',
  'Configurations',
  'Group members',
  'Summary',
];

const imageOptions = [
  'Ubuntu 22.04 LTS',
  'Windows 11 Enterprise',
  'Windows Server 2022',
];
const skuOptions = [
  '(default) 8vCPU_32GB_256SSD',
  '16vCPU_64GB_512SSD',
  '32vCPU_128GB_1TB',
];
const devBoxesPerDeveloperOptions = [1, 2, 3];

const useGeneralInformation = () => {
  const identityApi = useApi(identityApiRef);
  const [ownerName, setOwnerName] = useState('');
  const [groupNameSuffix, setGroupNameSuffix] = useState('');
  const { value: profile } = useAsync(
    () => identityApi.getProfileInfo(),
    [identityApi],
  );

  return {
    ownerName,
    setOwnerName,
    groupNameSuffix,
    setGroupNameSuffix,
    groupOwnerValue: ownerName.trim() || profile?.displayName || '-',
    groupNameValue: groupNameSuffix.trim()
      ? `Sec-${groupNameSuffix.trim()}`
      : '-',
  };
};

type GeneralInformation = ReturnType<typeof useGeneralInformation>;

const GeneralInformationFields = ({
  general,
}: {
  general: GeneralInformation;
}) => {
  return (
    <div className={shared.stepSection}>
      <div className={shared.formRow}>
        <div className={shared.formLabelCol}>
          <span className={shared.formLabel}>Owner</span>
          <span className={shared.hintText}>
            As the creator of the group, you are automatically the owner
          </span>
        </div>
        <div className={shared.formContent}>
          <span className={shared.formQuestion}>Owner of the group</span>
          <TextField
            aria-label="Owner of the group"
            className={shared.textFieldControl}
            value={general.ownerName}
            onChange={general.setOwnerName}
          />
        </div>
      </div>

      <div className={shared.formRow}>
        <span className={shared.formLabel}>
          Group name
          <RequiredStar />
        </span>
        <div className={shared.formContent}>
          <div className={shared.formQuestionGroup}>
            <span className={shared.formQuestion}>
              Choose a name for your group
            </span>
            <span className={shared.hintText}>
              Sec- is part of naming convention and cannot be changed
            </span>
          </div>
          <div className={styles.prefixedTextBox}>
            <span className={styles.prefix}>Sec-</span>
            <TextField
              aria-label="Group name"
              className={styles.prefixedTextField}
              placeholder="Type your group name…"
              value={general.groupNameSuffix}
              onChange={general.setGroupNameSuffix}
              isInvalid={!general.groupNameSuffix.trim()}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Placeholder team members, matching the mock data shown on the team page.
const teamMemberOptions = [
  'Kenneth Lauge',
  'Mikkel Buchvardt',
  'Sofie Andersen',
  'Peter Holm',
  'Anne Kjær',
  'Camilla Bruun',
];

const useGroupMembers = () => {
  const usersWithinTeamField = useDropdownField();
  const [outsideTeamInitials, setOutsideTeamInitials] = useState('');

  return {
    usersWithinTeamField,
    outsideTeamInitials,
    setOutsideTeamInitials,
    membersToAdd: [
      usersWithinTeamField.value,
      outsideTeamInitials.trim() || null,
    ].filter((member): member is string => Boolean(member)),
  };
};

type GroupMembers = ReturnType<typeof useGroupMembers>;

const GroupMembersFields = ({ members }: { members: GroupMembers }) => {
  return (
    <div className={shared.stepSection}>
      <div className={shared.formRow}>
        <div className={shared.formLabelCol}>
          <span className={shared.formLabel}>Members</span>
          <span className={shared.hintText}>
            You can add members now or in the group at a later point.
          </span>
          <span className={`${shared.hintText} ${styles.hintParagraphGap}`}>
            Making someone an admin means that they can edit configurations and
            change settings on the Dev Boxes in the group.
          </span>
        </div>
        <div className={styles.stackedFields}>
          <div className={styles.subField}>
            <span className={shared.formQuestion}>
              Users <strong>within</strong> your product team
            </span>
            <DropdownField
              name="usersWithinTeam"
              placeholder="Choose users"
              options={teamMemberOptions}
              field={members.usersWithinTeamField}
            />
          </div>
          <div className={styles.subField}>
            <span className={shared.formQuestion}>
              Users <strong>outside</strong> your product team
            </span>
            <TextField
              aria-label="User initials"
              className={shared.textFieldControl}
              placeholder="Type user initials"
              value={members.outsideTeamInitials}
              onChange={members.setOutsideTeamInitials}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const GeneralInformationSummary = ({
  groupType,
  general,
}: {
  groupType: string;
  general: GeneralInformation;
}) => {
  return (
    <div>
      <h2 className={shared.summaryHeading}>General information</h2>
      <Grid.Root columns="3" gap="14">
        <div className={shared.summaryColumn}>
          <span className={shared.hintText}>Group type</span>
          <span className={shared.summaryColumnValue}>{groupType}</span>
        </div>
        <div className={shared.summaryColumn}>
          <span className={shared.hintText}>Group owner</span>
          <span className={shared.summaryColumnValue}>
            {general.groupOwnerValue}
          </span>
        </div>
        <div className={shared.summaryColumn}>
          <span className={shared.hintText}>Group name</span>
          <span className={shared.summaryColumnValue}>
            {general.groupNameValue}
          </span>
        </div>
      </Grid.Root>
    </div>
  );
};

const MembersSummary = ({ members }: { members: GroupMembers }) => {
  return (
    <div>
      <h2 className={shared.summaryHeading}>Members</h2>
      <Grid.Root columns="3" gap="14">
        <div className={shared.summaryColumn}>
          <span className={shared.hintText}>Members to add</span>
          {members.membersToAdd.length > 0 ? (
            members.membersToAdd.map(member => (
              <span key={member} className={shared.summaryColumnValue}>
                {member}
              </span>
            ))
          ) : (
            <span className={shared.summaryColumnValue}>-</span>
          )}
        </div>
        <div className={shared.summaryColumn}>
          <span className={shared.hintText}>Admin rights</span>
          {members.membersToAdd.length > 0 ? (
            members.membersToAdd.map(member => (
              <span key={member} className={shared.summaryColumnValue}>
                No
              </span>
            ))
          ) : (
            <span className={shared.summaryColumnValue}>-</span>
          )}
        </div>
      </Grid.Root>
    </div>
  );
};

// Placeholder until this is wired up to a DevBox backend: only required fields are checked and submit always succeeds.
export const useCreateDevBoxToolingGroup = () => {
  const [devBoxesPerDeveloper, setDevBoxesPerDeveloper] = useState(1);
  const general = useGeneralInformation();
  const imageField = useDropdownField();

  return {
    general,
    members: useGroupMembers(),
    imageField,
    skuField: useDropdownField(skuOptions[0]),
    devBoxesPerDeveloper,
    setDevBoxesPerDeveloper,
    isStepValid: (step: number) =>
      (step !== 1 || Boolean(general.groupNameSuffix.trim())) &&
      (step !== 2 || Boolean(imageField.value)),
    submit: async () => true,
  };
};

export type CreateDevBoxToolingGroupForm = ReturnType<
  typeof useCreateDevBoxToolingGroup
>;

/** Renders `step` (1-based within `devBoxToolingGroupSteps`) of the DevBox flow. */
export const CreateDevBoxToolingGroup = ({
  step,
  form,
}: {
  step: number;
  form: CreateDevBoxToolingGroupForm;
}) => {
  switch (step) {
    case 1:
      return <GeneralInformationFields general={form.general} />;
    case 2:
      return (
        <div className={shared.stepSection}>
          <div className={shared.formRow}>
            <div className={shared.formLabelCol}>
              <span className={shared.formLabel}>
                Image
                <RequiredStar />
              </span>
              <span className={shared.hintText}>
                The image chosen will be the default image on all created dev
                boxes within this tooling group
              </span>
            </div>
            <div className={shared.formContent}>
              <span className={shared.formQuestion}>
                Default image for dev boxes
              </span>
              <DropdownField
                name="image"
                placeholder="Choose image"
                options={imageOptions}
                field={form.imageField}
                isInvalid={!form.imageField.value}
              />
            </div>
          </div>

          <div className={shared.formRow}>
            <div className={shared.formLabelCol}>
              <span className={shared.formLabel}>SKU</span>
              <span className={shared.hintText}>
                Unless specified the standard SKU package will be chosen as
                default
              </span>
            </div>
            <div className={shared.formContent}>
              <span className={shared.formQuestion}>
                Sizing of the machines
              </span>
              <DropdownField
                name="sku"
                placeholder="Choose SKU"
                options={skuOptions}
                field={form.skuField}
              />
            </div>
          </div>

          <div className={shared.formRow}>
            <span className={shared.formLabel}>Dev Boxes per Developer</span>
            <div className={shared.formContent}>
              <div className={shared.formQuestionGroup}>
                <span className={shared.formQuestion}>
                  Number of maximum Dev Boxes per Developer
                </span>
                <span className={shared.hintText}>
                  Please provide only if the number is different from the
                  default 1 Dev Box per Developer
                </span>
              </div>
              <RadioGroup
                aria-label="Number of maximum Dev Boxes per Developer"
                className={styles.radioGroup}
                orientation="horizontal"
                value={String(form.devBoxesPerDeveloper)}
                onChange={value => form.setDevBoxesPerDeveloper(Number(value))}
              >
                {devBoxesPerDeveloperOptions.map(count => (
                  <Radio key={count} value={String(count)}>
                    {count}
                  </Radio>
                ))}
              </RadioGroup>
            </div>
          </div>
        </div>
      );
    case 3:
      return <GroupMembersFields members={form.members} />;
    default:
      return (
        <div className={shared.stepSection}>
          <div className={shared.summaryBox}>
            <GeneralInformationSummary
              groupType="DevBox"
              general={form.general}
            />
            <div>
              <h2 className={shared.summaryHeading}>Configurations</h2>
              <Grid.Root columns="3" gap="14">
                <div className={shared.summaryColumn}>
                  <span className={shared.hintText}>Image</span>
                  <span className={shared.summaryColumnValue}>
                    {form.imageField.value ?? '-'}
                  </span>
                </div>
                <div className={shared.summaryColumn}>
                  <span className={shared.hintText}>
                    Number of Dev Boxes per Developer
                  </span>
                  <span className={shared.summaryColumnValue}>
                    {form.devBoxesPerDeveloper}
                  </span>
                </div>
              </Grid.Root>
            </div>
            <MembersSummary members={form.members} />
          </div>
        </div>
      );
  }
};

/** Route: /product-teams/:id/tooling-groups/devbox/create */
export const CreateDevBoxServiceGroupPage = () => {
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const [activeStep, setActiveStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const form = useCreateDevBoxToolingGroup();
  const goToTeamPage = () =>
    navigate(`/product-teams/${encodeURIComponent(id)}`);

  const goToNextStep = async () => {
    if (activeStep < devBoxToolingGroupSteps.length) {
      setActiveStep(step => step + 1);
      return;
    }
    setSubmitting(true);
    try {
      if (await form.submit()) goToTeamPage();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.body}>
      <StepTimeline
        steps={devBoxToolingGroupSteps}
        activeStep={activeStep}
        onCancel={goToTeamPage}
        onPrevious={() => setActiveStep(step => Math.max(step - 1, 1))}
        onNext={goToNextStep}
        isNextDisabled={!form.isStepValid(activeStep)}
        isSubmitting={submitting}
      >
        <CreateDevBoxToolingGroup step={activeStep} form={form} />
      </StepTimeline>
    </div>
  );
};
